// VIDEO-EDITING — Zustand store for the editor. Mirrors
// features/department/store/department/ in shape: slices return action
// bags, the store composes them, and persist middleware writes a single
// localStorage key keyed by `currentProjectId`.
import { create, type StoreApi } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";

import {
  DRAFT_STORAGE_KEY,
  DRAFT_STORAGE_VERSION,
} from "../services/draftPersistence";
import {
  SOURCE_LANGUAGE_ID,
  normalizeCustomCaptionStyle,
  normalizeOverlays,
  type TranscriptSegment,
  type VideoProject,
} from "../types";
import { applyOpsToSegments, type TimelineOp } from "../types/timeline";
import { createProjectActions } from "./slices/projectSlice";
import { createDraftActions } from "./slices/draftSlice";
import { createUiActions } from "./slices/uiSlice";
import { createSseActions, wireVideoEditorSse } from "./slices/sseSlice";
import { createClipActions } from "./slices/clipsSlice";
import type {
  ProjectDraft,
  SseState,
  UiState,
  VideoEditorState,
} from "./types";
import {
  EMPTY_DRAFT,
  EMPTY_SEGMENTS,
  EMPTY_SSE,
  EMPTY_UI,
  computeDirty,
  prunePatch,
} from "./utils";

type Set = StoreApi<VideoEditorState>["setState"];
type Get = StoreApi<VideoEditorState>["getState"];

function createState(set: Set, get: Get): VideoEditorState {
  return {
    currentProjectId: null,
    project: null,
    drafts: {},
    ui: {},
    sse: {},
    saving: false,
    saveError: null,
    deleting: false,
    ...createProjectActions(set),
    ...createDraftActions(set),
    ...createUiActions(set),
    ...createSseActions(set),
    ...createClipActions(set, get),
    setSaving: (saving) => set((s) => ({ ...s, saving })),
    setSaveError: (saveError) => set((s) => ({ ...s, saveError })),
    setDeleting: (deleting) => set((s) => ({ ...s, deleting })),
  };
}

export const useVideoEditorStore = create<VideoEditorState>()(
  persist((set, get) => createState(set, get), {
    name: DRAFT_STORAGE_KEY,
    version: DRAFT_STORAGE_VERSION,
    storage: createJSONStorage(() => localStorage),
    partialize: (state) => ({ drafts: state.drafts }),
    // Zod-validate drafts on hydrate. An invalid draft (corruption, an
    // older shape) is dropped silently; the user simply loses that one
    // entry and the editor falls back to the server snapshot.
    merge: (persisted, current) => {
      const out: VideoEditorState = { ...current };
      const p = persisted as { drafts?: Record<string, unknown> } | undefined;
      if (!p?.drafts) return out;
      const drafts: Record<string, ProjectDraft> = {};
      for (const [id, value] of Object.entries(p.drafts)) {
        if (!value || typeof value !== "object") continue;
        const v = value as Partial<ProjectDraft>;
        drafts[id] = {
          ...EMPTY_DRAFT,
          ...v,
          draftCustomStyle:
            v.draftCustomStyle === undefined
              ? null
              : normalizeCustomCaptionStyle(v.draftCustomStyle),
          draftOverlays: Array.isArray(v.draftOverlays)
            ? normalizeOverlays(v.draftOverlays)
            : null,
          lastEditedAt:
            typeof v.lastEditedAt === "number" ? v.lastEditedAt : 0,
          timelineOps: Array.isArray(v.timelineOps) ? v.timelineOps : [],
          draftLanguageSegments:
            v.draftLanguageSegments &&
            typeof v.draftLanguageSegments === "object"
              ? v.draftLanguageSegments
              : {},
        };
      }
      out.drafts = drafts;
      return out;
    },
  }),
);

// Wire SSE subscribers once at module init. The store is a singleton so a
// single subscriber set is fine.
wireVideoEditorSse(useVideoEditorStore as unknown as StoreApi<VideoEditorState>);

// ── Selectors ────────────────────────────────────────────────────────────
// Granular per-field selectors per frontend/CLAUDE.md Gotcha #1. Compose
// with useShallow when a component needs a combined snapshot.

export const selectCurrentProjectId = (s: VideoEditorState) =>
  s.currentProjectId;
export const selectProject = (s: VideoEditorState) => s.project;
export const selectSaving = (s: VideoEditorState) => s.saving;
export const selectSaveError = (s: VideoEditorState) => s.saveError;

export function selectDraft(projectId: string | null) {
  return (s: VideoEditorState): ProjectDraft =>
    (projectId && s.drafts[projectId]) || EMPTY_DRAFT;
}

export function selectUi(projectId: string | null) {
  return (s: VideoEditorState): UiState =>
    (projectId && s.ui[projectId]) || EMPTY_UI;
}

export function selectSse(projectId: string | null) {
  return (s: VideoEditorState): SseState =>
    (projectId && s.sse[projectId]) || EMPTY_SSE;
}

export function selectIsDirty(projectId: string | null) {
  return (s: VideoEditorState): boolean => {
    if (!projectId || !s.project || s.project.id !== projectId) return false;
    const ui = s.ui[projectId] ?? EMPTY_UI;
    return computeDirty(s.drafts[projectId], s.project, ui.activeLanguage);
  };
}

const timelineSegmentCache = new WeakMap<
  TranscriptSegment[],
  WeakMap<TimelineOp[], TranscriptSegment[]>
>();

function applyCachedTimelineOps(
  segments: TranscriptSegment[],
  ops: TimelineOp[],
): TranscriptSegment[] {
  if (segments.length === 0) return EMPTY_SEGMENTS;
  let opCache = timelineSegmentCache.get(segments);
  if (!opCache) {
    opCache = new WeakMap<TimelineOp[], TranscriptSegment[]>();
    timelineSegmentCache.set(segments, opCache);
  }
  const cached = opCache.get(ops);
  if (cached) return cached;
  const next = applyOpsToSegments(segments, ops);
  opCache.set(ops, next);
  return next;
}

// Effective active-language segments — overlays/clip-timeline render off
// these. Falls back to project transcript when no draft is in flight.
export function selectEffectiveSegments(projectId: string | null) {
  return (s: VideoEditorState): TranscriptSegment[] => {
    if (!projectId || !s.project) return EMPTY_SEGMENTS;
    const draft = s.drafts[projectId] ?? EMPTY_DRAFT;
    if (draft.draftSegments) return draft.draftSegments;

    const ui = s.ui[projectId] ?? EMPTY_UI;
    const activeLanguage = ui.activeLanguage || SOURCE_LANGUAGE_ID;

    const segs =
      activeLanguage === SOURCE_LANGUAGE_ID
        ? s.project.transcript?.segments ?? EMPTY_SEGMENTS
        : s.project.translations?.find((t) => t.language === activeLanguage)
            ?.segments ?? EMPTY_SEGMENTS;

    if (draft.timelineOps.length === 0) return segs;
    return applyCachedTimelineOps(segs, draft.timelineOps);
  };
}

export function useShallowSnapshot<T>(
  selector: (s: VideoEditorState) => T,
): T {
  return useVideoEditorStore(useShallow(selector));
}

// Convenience — gets a patch suitable for PATCH /projects/{id}/.
export function buildSavePatch(
  state: VideoEditorState,
  projectId: string,
): Record<string, unknown> | null {
  const project: VideoProject | null = state.project;
  if (!project || project.id !== projectId) return null;
  const draft = state.drafts[projectId];
  if (!draft) return null;
  const patch = prunePatch(draft, project);
  if (Object.keys(patch).length === 0 && draft.timelineOps.length === 0) {
    return null;
  }
  return patch;
}
